import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  availableStoreSliceEmptyState,
  emptyState,
  locationEmptyState,
  locationInitialState,
} from "./states";
import { storeGlobalActions } from "../actions";
import { applyCurrentStore } from "../storeSync";
import { getProductSource } from "@/utils/checkoutType";

export const cartStoresSlice = createSlice({
  name: "cartStore",
  initialState: emptyState,
  reducers: {
    //  Location Slice Action
    setLocation: (_state: CartStoreAttr, action: PayloadAction<Partial<LocationType>>): CartStoreAttr => {
      if (_state.location === undefined) {
        _state.location = { ...locationInitialState, ...action.payload };
      } else {
        _state.location = Object.assign(_state.location, action.payload);
      }
      return _state;
    },
    emptyLocation: (_state: CartStoreAttr): CartStoreAttr => {
      _state.location = locationEmptyState;
      return _state;
    },
    setInitialLocation: (_state: CartStoreAttr): CartStoreAttr => {
      _state.location = locationInitialState;
      return _state;
    },
    // Available Slice Action
    setAvailableStores: (
      _state: CartStoreAttr,
      action: PayloadAction<setAvailableStoresPayload>
    ): CartStoreAttr => {
      const sorting = [
        "market",
        "restaurant",
        "fit-center",
        "sportswear",
        "sports",
        "books",
        "home-and-kitchen",
        "home-appliances",
        "personal-care",
      ];

      // 🔥 copy array before sort
      const stores = [...action.payload.availableStores.stores].sort(
        (a, b) => sorting.indexOf(a.type) - sorting.indexOf(b.type)
      );

      _state.availableStores = {
        ...action.payload.availableStores,
        stores,
        validateAt: action.payload.validateAt,
      };
      return _state;
    },
    emptyAvailability: (_state): CartStoreAttr => {
      _state.availableStores = availableStoreSliceEmptyState;

      return _state;
    },
    // CartSlice Actions
    setStoreId: (_state, action: PayloadAction<number>): CartStoreAttr => {
      _state.storeId = Number(action.payload);
      return _state;
    },
    emptyCart: (_state, action: PayloadAction<{ emptyStore?: boolean }>): CartStoreAttr => {
      _state.items = [];
      if (action.payload?.emptyStore) {
        _state.storeId = undefined;
      }
      return _state;
    },
    setInitialState: (): CartStoreAttr => emptyState,

    // v2

    addProduct: (state, action: PayloadAction<Cart>) => {
      const stepWeight =
        action.payload.details.isWeightable && action.payload.details.averageWeight
          ? action.payload.details.averageWeight || 1
          : 1;

      state.items.push({ ...action.payload, storeId: String(state.storeId), quantity: stepWeight });
    },

    // addProducts: (state, action: PayloadAction<Cart[]>) => {
    //   state.items = {
    //     ...state.items,
    //     ...action.payload.map((item) => ({
    //       ...item,
    //       store: {
    //         pk: state.storeId,
    //       },
    //       quantity: 1,
    //     })),
    //   };
    // },

    removeProduct: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => String(item.product.sku) !== String(action.payload));
    },
    removeProducts: (state, action: PayloadAction<string[]>) => {
      state.items = state.items.filter((item) => !action.payload.includes(String(item.product.sku)));
    },
    changeQuantity: (state, action: PayloadAction<{ productSKU: number; quantity: number }>) => {
      const { productSKU, quantity } = action.payload;
      const items = state.items;
      for (const item of items) {
        if (String(item.product.sku) === String(productSKU)) {
          item.quantity = quantity;
          break;
        }
      }
      state.items = items;
    },
    changeQuatityAvailable: (
      state,
      action: PayloadAction<{
        productSKU: number;
        maxQtyToOrder: number;
        isAvailable: boolean;
        currency?: string;
        finalPrice?: number;
        originalPrice?: number;
        onSale?: boolean;
        measureTypeDisplayShort?: string | null;
        customLabel?: string;
        discountLabel?: string;
        customLabelBackgroundColor?: string;
        averageWeight?: number | null;
        isWeightable?: boolean;
        name?: string;
        isImported?: boolean;
        quantityAvailable?: number;
        allowOrderOutOfStock?: boolean;
      }>
    ) => {
      const { productSKU, maxQtyToOrder, isAvailable, currency, finalPrice, originalPrice, onSale } =
        action.payload;
      const itemIndex = state.items.findIndex((item) => String(item.product.sku) === String(productSKU));

      if (itemIndex !== -1) {
        const updatedItem = { ...state.items[itemIndex] };

        updatedItem.details = {
          ...updatedItem.details,
          maxQtyToOrder,
          available: isAvailable,
          customLabel: action.payload.customLabel ?? "",
          discountLabel: action.payload.discountLabel ?? "",
          customLabelBackgroundColor: action.payload.customLabelBackgroundColor ?? "",
          quantityAvailable: action.payload.quantityAvailable ?? 0,
          allowOrderOutOfStock: action.payload.allowOrderOutOfStock ?? false,
        };

        if (currency !== undefined) {
          updatedItem.price.currency = currency;
        }

        if (finalPrice !== undefined) {
          updatedItem.price.finalPrice = finalPrice;
        }

        if (originalPrice !== undefined) {
          updatedItem.price.originalPrice = originalPrice;
        }

        if (onSale !== undefined) {
          updatedItem.details.onSale = onSale;
        }

        if (!!updatedItem.price.finalPrice && !!updatedItem.price.originalPrice) {
          updatedItem.price.discount = updatedItem.price.originalPrice - updatedItem.price.finalPrice;
        }

        if (action.payload.name !== undefined) {
          updatedItem.product.name = action.payload.name;
        }

        if (action.payload.averageWeight !== undefined) {
          updatedItem.details.averageWeight = action.payload.averageWeight || 1;
        }

        if (action.payload.isWeightable !== undefined) {
          updatedItem.details.isWeightable = action.payload.isWeightable;
        }

        if (action.payload.measureTypeDisplayShort !== undefined) {
          updatedItem.details.measureTypeDisplayShort = action.payload.measureTypeDisplayShort;
        }
        if (action.payload.isImported !== undefined) {
          updatedItem.details.isImported = action.payload.isImported;
        }

        state.items[itemIndex] = updatedItem;
      }
    },
    removeDuplicatedProducts: (state) => {
      // First check if there are duplicated products
      const seenSkus = new Set<string>(); // Set to check unique SKUs for duplicated products
      const duplicatesExist = state.items.some((item) => {
        const sku = String(item.product.sku);

        if (seenSkus.has(sku)) {
          return true; // If the SKU is already in the set, duplicates exist
        }
        seenSkus.add(sku); // Mark the SKU as seen
        return false;
      });

      // Only update the state if duplicates are found
      if (duplicatesExist) {
        const uniqueItems = new Set<string>(); // Set to check unique SKUs for filtering
        state.items = state.items.reduce((acc: ProcessedItems[], item) => {
          const sku = String(item.product.sku);
          if (!uniqueItems.has(sku)) {
            uniqueItems.add(sku); // Mark the SKU as unique
            acc.push(item as ProcessedItems); // Add the item to the result if it's not a duplicate
          }
          return acc;
        }, []);
      }
    },
    setLastUsedAddress: (state, action: PayloadAction<{ city: string; address: any }>) => {
      const { city, address } = action.payload;

      // Asegurarse de crear un nuevo objeto en lugar de mutar el estado directamente
      state.lastUsedAddresses = { ...state.lastUsedAddresses, [city]: address };
    },
    emptyLastUsedAddresses: (state) => {
      state.lastUsedAddresses = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(storeGlobalActions.emptyCheckout, (state, action) => {
      const { source } = action.payload || {};
      if (!source) {
        state.items = [];
        return;
      }
      state.items = state.items.filter((item) => getProductSource(item) !== source);
    });
    builder.addCase(applyCurrentStore, (state, action) => {
      const { storeId, availableStores, location, silent } = action.payload;

      cartStoresSlice.caseReducers.setAvailableStores(state, {
        type: action.type,
        payload: { availableStores, validateAt: availableStores.validateAt },
      });
      state.storeId = Number(storeId);

      if (location) {
        state.location = { ...(state.location ?? locationInitialState), ...location };
      }

      state.lastSilentStoreId = silent ? Number(storeId) : undefined;
    });
  },
});

export const cartStoresActions = cartStoresSlice.actions;
